// PerformanceExcelExportUtils.js - Handle Excel export functionality for Performance Analysis

import { excelApi } from './api';

/**
 * Utility class to handle Excel export operations for Performance Analysis
 */
export class PerformanceExcelExportUtils {
  constructor(groupId, groupName) {
    this.groupId = groupId;
    this.groupName = groupName;
  }

  /**
   * Generate and download Excel for the current period
   * @param {Object} currentPeriod - The current period data
   * @param {Function} onStart - Callback when operation starts
   * @param {Function} onSuccess - Callback when operation succeeds
   * @param {Function} onError - Callback when operation fails
   */
  async exportToExcel(currentPeriod, isSchoolFees = false, onStart, onSuccess, onError) {
    try {
      onStart && onStart();
  
      const options = {
        isSchoolFees: isSchoolFees
      };
      
      if (currentPeriod) {
        options.periodStart = currentPeriod.period_start;
        options.periodEnd = currentPeriod.period_end;
      }
  
      console.log('📊 Generating Excel with options:', options);
  
      // Generate and download Excel
      const result = await excelApi.generateAndDownloadExcel(this.groupId, options);
      
      // Use the actual filename from backend
      const filename = result.filename || this._generateFallbackFilename(currentPeriod, isSchoolFees, 'xlsx');
      
      // Trigger download
      excelApi.triggerDownload(result.blob, filename);
      
      onSuccess && onSuccess(filename);
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      onError && onError(error.message || 'Failed to generate Excel');
    }
  }

  /**
   * Generate Excel without immediate download (for getting download link)
   * @param {Object} currentPeriod - The current period data
   * @returns {Promise<Object>} Excel generation response with download link
   */
  async generateExcelInfo(currentPeriod) {
    const options = {};
    if (currentPeriod) {
      options.periodStart = currentPeriod.period_start;
      options.periodEnd = currentPeriod.period_end;
      options.download = false; // Don't download immediately
    }

    return await excelApi.generateGroupExcel(this.groupId, options);
  }

  /**
   * List all available Excel files for this group
   * @returns {Promise<Object>} List of available Excel files
   */
  async listAvailableExcels() {
    return await excelApi.listGroupExcels(this.groupId);
  }

  /**
   * Download a specific Excel file by filename
   * @param {string} filename - The filename to download
   */
  async downloadSpecificExcel(filename) {
    try {
      const excelBlob = await excelApi.downloadExcel(this.groupId, filename);
      excelApi.triggerDownload(excelBlob, filename);
    } catch (error) {
      console.error('Error downloading specific Excel:', error);
      throw error;
    }
  }

  /**
   * Delete a specific Excel file
   * @param {string} filename - The filename to delete
   * @returns {Promise<Object>} Delete response
   */
  async deleteExcel(filename) {
    return await excelApi.deleteExcel(this.groupId, filename);
  }

  /**
   * Share performance analysis by generating Excel and returning download link
   * @param {Object} currentPeriod - The current period data
   * @returns {Promise<Object>} Sharing information with download link
   */
  async sharePerformanceAnalysis(currentPeriod) {
    try {
      // Generate Excel and get download link from backend
      const excelInfo = await this.generateExcelInfo(currentPeriod);
      
      // Return sharing information
      return {
        success: true,
        downloadUrl: excelInfo.download_url,
        message: 'Excel generated successfully. You can share the download link.',
        filename: excelInfo.filename
      };
      
    } catch (error) {
      console.error('Error preparing share:', error);
      throw error;
    }
  }
}

/**
 * Factory function to create PerformanceExcelExportUtils instance
 * @param {string|number} groupId - Group ID
 * @param {string} groupName - Group name
 * @returns {PerformanceExcelExportUtils} Export utils instance
 */
export const createPerformanceExcelExportUtils = (groupId, groupName) => {
  return new PerformanceExcelExportUtils(groupId, groupName);
};

export default PerformanceExcelExportUtils;